import { useState } from 'react';
import { Mail, ExternalLink, Copy, ChevronDown, ChevronRight, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PipelineStageCard, type PipelineStageData } from './PipelineStageCard';
import type { AnsweredLetterIn } from '../../../lib/api';

interface InboundLetterCardProps {
  letter: AnsweredLetterIn;
  onCopyText: (text: string, label: string) => void;
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getLetterStats(text: string | null): { words: number; paragraphs: number } {
  if (!text?.trim()) {
    return { words: 0, paragraphs: 0 };
  }
  const normalized = text.trim();
  const words = normalized.split(/\s+/).filter(Boolean).length;
  const paragraphs = normalized.split(/\n\s*\n/).filter((chunk) => chunk.trim().length > 0).length;
  return { words, paragraphs };
}

function buildInboundPipelineStages(extraction: Record<string, unknown>): PipelineStageData[] {
  const stages: PipelineStageData[] = [];

  // Extraction stage
  const extractionData = extraction.extraction as Record<string, unknown> | undefined;
  if (extractionData) {
    const extractUsage = extractionData.usage as Record<string, unknown> | undefined;
    stages.push({
      id: 'extraction',
      title: 'Extract World Update',
      description: 'Extract entities, events, and world changes from inbound letter',
      model: String(extractionData.model ?? 'unknown'),
      status: extractionData.status === 'success' ? 'success' : 'error',
      output: extractionData.delta,
      outputLabel: 'World Update Delta',
      metrics: {
        inputTokens: typeof extractUsage?.prompt_tokens === 'number' ? extractUsage.prompt_tokens : undefined,
        outputTokens: typeof extractUsage?.completion_tokens === 'number' ? extractUsage.completion_tokens : undefined,
      },
    });
  }

  // Correspondent Profile stage
  const profileData = extraction.correspondent_profile as Record<string, unknown> | undefined;
  if (profileData) {
    const profileUsage = profileData.usage as Record<string, unknown> | undefined;
    stages.push({
      id: 'correspondent-profile',
      title: 'Correspondent Profile',
      description: 'Update understanding of the correspondent based on their letter',
      model: String(profileData.model ?? 'unknown'),
      status: 'success',
      output: profileData.profile,
      outputLabel: 'Profile Update',
      metrics: {
        inputTokens: typeof profileUsage?.prompt_tokens === 'number' ? profileUsage.prompt_tokens : undefined,
        outputTokens: typeof profileUsage?.completion_tokens === 'number' ? profileUsage.completion_tokens : undefined,
      },
    });
  }

  // Inner Dialog stage
  const dialogData = extraction.correspondent_inner_dialog as Record<string, unknown> | undefined;
  if (dialogData) {
    const dialogUsage = dialogData.usage as Record<string, unknown> | undefined;
    stages.push({
      id: 'inner-dialog',
      title: 'Inner Dialog',
      description: "Generate correspondent's internal thoughts and reactions",
      model: String(dialogData.model ?? 'unknown'),
      status: 'success',
      output: dialogData.dialog,
      outputLabel: 'Inner Dialog',
      metrics: {
        inputTokens: typeof dialogUsage?.prompt_tokens === 'number' ? dialogUsage.prompt_tokens : undefined,
        outputTokens: typeof dialogUsage?.completion_tokens === 'number' ? dialogUsage.completion_tokens : undefined,
      },
    });
  }

  // World Builder stage (evolve_pre)
  const worldBuilderData = extraction.world_builder as Record<string, unknown> | undefined;
  if (worldBuilderData) {
    const wbUsage = worldBuilderData.usage as Record<string, unknown> | undefined;
    stages.push({
      id: 'world-builder',
      title: 'World Builder',
      description: 'World updates derived from the inbound letter',
      model: String(worldBuilderData.model ?? 'unknown'),
      status: 'success',
      output: worldBuilderData.delta,
      outputLabel: 'World Delta',
      metrics: {
        inputTokens: typeof wbUsage?.prompt_tokens === 'number' ? wbUsage.prompt_tokens : undefined,
        outputTokens: typeof wbUsage?.completion_tokens === 'number' ? wbUsage.completion_tokens : undefined,
      },
    });
  }

  // Connection Enrichment stage
  const connectionsData = extraction.connection_enrichment as Record<string, unknown> | undefined;
  if (connectionsData) {
    const connUsage = connectionsData.usage as Record<string, unknown> | undefined;
    stages.push({
      id: 'connection-enrichment',
      title: 'Connection Enrichment',
      description: 'Infer relationships and threads from the inbound letter',
      model: String(connectionsData.model ?? 'unknown'),
      status: 'success',
      output: {
        relationshipsAdded: connectionsData.relationshipsAdded,
        threadsAdded: connectionsData.threadsAdded,
      },
      metrics: {
        inputTokens: typeof connUsage?.prompt_tokens === 'number' ? connUsage.prompt_tokens : undefined,
        outputTokens: typeof connUsage?.completion_tokens === 'number' ? connUsage.completion_tokens : undefined,
      },
    });
  }

  // World Deepen stage
  const worldDeepenData = extraction.world_deepen as Record<string, unknown> | undefined;
  if (worldDeepenData) {
    const wdUsage = worldDeepenData.usage as Record<string, unknown> | undefined;
    stages.push({
      id: 'world-deepen',
      title: 'World Deepen',
      description: 'Additional world texture layered in after extraction',
      model: String(worldDeepenData.model ?? 'unknown'),
      status: 'success',
      output: worldDeepenData.delta,
      outputLabel: 'Deepen Delta',
      metrics: {
        inputTokens: typeof wdUsage?.prompt_tokens === 'number' ? wdUsage.prompt_tokens : undefined,
        outputTokens: typeof wdUsage?.completion_tokens === 'number' ? wdUsage.completion_tokens : undefined,
      },
    });
  }

  // Summaries stage
  const summariesData = extraction.summaries as Record<string, unknown> | undefined;
  if (summariesData) {
    const sumUsage = summariesData.usage as Record<string, unknown> | undefined;
    stages.push({
      id: 'summaries',
      title: 'Summaries',
      description: 'Regenerate world briefs after extraction',
      model: String(summariesData.model ?? 'unknown'),
      status: 'success',
      output: summariesData,
      metrics: {
        inputTokens: typeof sumUsage?.prompt_tokens === 'number' ? sumUsage.prompt_tokens : undefined,
        outputTokens: typeof sumUsage?.completion_tokens === 'number' ? sumUsage.completion_tokens : undefined,
      },
    });
  }

  return stages;
}

export function InboundLetterCard({ letter, onCopyText }: InboundLetterCardProps) {
  const [showPipeline, setShowPipeline] = useState(false);

  const extraction = letter.extraction && typeof letter.extraction === 'object'
    ? (letter.extraction as Record<string, unknown>)
    : null;

  const pipelineStages = extraction ? buildInboundPipelineStages(extraction) : [];
  const stats = getLetterStats(letter.cleanText);

  return (
    <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-stone/30 space-y-2">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            <Mail size={18} className="text-rune mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="text-sm text-parchment font-medium truncate">
                From: {letter.fromEmail}
              </div>
              {letter.subject && (
                <div className="text-sm text-mist truncate">
                  Subject: {letter.subject}
                </div>
              )}
            </div>
          </div>
          <Link
            to={`/admin/letters-in/${letter.id}`}
            className="flex items-center gap-1 text-xs text-rune hover:text-parchment flex-shrink-0"
            title="View full inbound letter details"
          >
            <ExternalLink size={14} />
            <span>View</span>
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-xs text-mist">
          <div className="flex items-center gap-1">
            <Calendar size={12} />
            <span>Received: {formatDate(letter.receivedAt)}</span>
          </div>
          {letter.processedAt && (
            <div className="flex items-center gap-1">
              <Calendar size={12} />
              <span>Processed: {formatDate(letter.processedAt)}</span>
            </div>
          )}
          <div>
            {stats.words} words · {stats.paragraphs} paragraphs
          </div>
        </div>
      </div>

      {/* Letter Content */}
      {letter.cleanText && (
        <div className="p-4 border-b border-stone/30">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-parchment">Letter Text</h3>
            <button
              onClick={() => onCopyText(letter.cleanText ?? '', 'Inbound letter')}
              className="flex items-center gap-1 text-xs text-mist hover:text-parchment px-2 py-1 rounded border border-stone/30"
            >
              <Copy size={12} />
              Copy
            </button>
          </div>
          <pre className="p-4 bg-void rounded-lg text-mist text-sm whitespace-pre-wrap overflow-auto max-h-[400px]">
            {letter.cleanText}
          </pre>
        </div>
      )}

      {/* Pipeline Stages (Collapsible) */}
      {pipelineStages.length > 0 && (
        <div className="p-4">
          <button
            onClick={() => setShowPipeline(!showPipeline)}
            className="flex items-center gap-2 text-sm font-medium text-parchment hover:text-rune transition-colors w-full text-left"
          >
            {showPipeline ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span>Processing Pipeline</span>
            <span className="text-xs text-mist font-normal">
              ({pipelineStages.length} stages)
            </span>
          </button>

          {showPipeline && (
            <div className="mt-4 space-y-3">
              {pipelineStages.map((stage, index) => (
                <PipelineStageCard
                  key={stage.id}
                  stage={stage}
                  step={index + 1}
                  compact
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
